import { inject, observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import { Moment } from "moment";
import { ConvertToCents, CovertAndFormatCentsToDollars } from "src/common/Methods/FileUtil";
import { Customer, ExtractFriendlyName } from "src/model/Customer";
import {
    Currency,
    Disbursement,
    DisbursementPamentMethods,
    Invoice,
    Payment,
    PaymentMethod,
    PaymentMethodFriendlyName,
    PaymentPamentMethods,
    Proceeds,
    Settlement,
    SettlementType,
    SettlementTypeFriendlyName
} from "src/model/Financial";
import { SettlementEditRequest, SettlementRequest } from "src/requests/FinancialRequests";
import { CustomerStore } from "src/stores/CustomerStore";

import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { CovertAndFormatCentsToDollarsWithDollarSign } from "../../common/Methods/FileUtil";
import { FormDialog } from "../common/FormDialog";
import { DateGroup, InputGroup, SelectGroup, TextareaGroup } from "../common/SimpleFormComponents";
import { errorMessage, successMessage } from "../common/Toast";
import { SimpleSettlementTable } from "./SimpleSettlementTable";
import { ConfigurationStore } from "src/stores/ConfigurationStore";

const StatementNumbers = ({ title, value, styleTitle, styleValue }: { title: string; value: number; styleTitle?: string; styleValue: string }) => {
    return (
        <div className="col-12 text-right d-flex">
            <div className={`ml-auto ${styleTitle ? `text-${styleTitle}` : ``}`}>{title}: </div>
            <div className={`text-right text-${styleValue}`} style={{ minWidth: "105px" }}>
                {CovertAndFormatCentsToDollars(value)}
            </div>
        </div>
    );
};

namespace EditableSettlement {
    export interface Fields {
        id?: number | null;
        type?: SettlementType | null;

        amount: string;
        currency: Currency | null;
        paymentMethod: PaymentMethod | null;
        notes: string;
        entryDate: Moment | string;

        coversInvoices: Array<number>;
        useProceeds: Array<number>;
        usePayments: Array<number>;
        useDisbursements: Array<number>;
    }

    export const Empty = (
        coversInvoices: Array<number> = [],
        useProceeds: Array<number> = [],
        usePayments: Array<number> = [],
        useDisbursements: Array<number> = []
    ): Fields => {
        return {
            id: null,
            type: null,

            currency: null,
            amount: "",
            paymentMethod: null,
            notes: "",
            entryDate: moment(new Date()),

            coversInvoices,
            useProceeds,
            usePayments,
            useDisbursements
        };
    };

    export const FromModel = (s: Settlement): Fields => {
        return {
            id: s.id,
            type: s.type,

            currency: s.createdLineItem ? s.createdLineItem.currency : null,
            amount: s.createdLineItem ? (s.createdLineItem.amountInCents / 100).toString() : "",
            paymentMethod: s.createdLineItem ? s.createdLineItem.paymentMethod : null,
            notes: s.notes || "",
            entryDate: s.createdLineItem && s.createdLineItem.entryDate ? moment(s.createdLineItem.entryDate) : "",

            coversInvoices: [],
            useProceeds: [],
            usePayments: [],
            useDisbursements: []
        };
    };

    export const ToCreateRequest = (fields: Fields): SettlementRequest => {
        if (!fields.type) {
            throw new Error("There are non-nullable null fields in ToCreateRequest");
        }
        return {
            type: fields.type,

            amountInCents: ConvertToCents(fields.amount),
            currency: fields.currency,
            paymentMethod: fields.paymentMethod,
            entryDate: fields.entryDate && (fields.entryDate as Moment).utc ? (fields.entryDate as Moment).valueOf() : null,
            notes: fields.notes,

            coversInvoices: fields.coversInvoices,
            useProceeds: fields.useProceeds,
            usePayments: fields.usePayments,
            useDisbursements: fields.useDisbursements
        };
    };

    export const ToEditRequest = (fields: Fields): SettlementEditRequest => {
        const request = {
            currency: fields.currency,
            paymentMethod: fields.paymentMethod,
            notes: fields.notes || ""
        };
        return request;
    };
}

export interface StatePage {
    wasValidated: boolean;
    showInvoicesMessage: boolean;
}

export interface Props {
    customerStore?: CustomerStore;
    configurationStore?: ConfigurationStore;
    showSettlementDialog: boolean;
    editableSettlement?: Settlement;
    closeDialog: (updatedCustomer?: Customer | null) => void;
    applicationsOpen?: number;
    cadToUsdExchangeRate?: number | undefined | false;
}

type State = StatePage & EditableSettlement.Fields;

@inject("configurationStore")
@inject("customerStore")
@observer
export default class SettlementDialog extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = this.emptyState(props);
    }

    emptyState = (props: Props = this.props): State => {
        // By default, make sure all invoices, proceeds and payments are checked

        const coveredInvoices: Array<number> = !props.customerStore
            ? []
            : props.customerStore!.customer!.account.account.invoices.filter(i => i.dateVoided === null && i.balance !== 0).map(i => i.id);
        const useProceeds: Array<number> = !props.customerStore
            ? []
            : props.customerStore!.customer!.account.account.proceeds.filter(p => p.dateArchived === null && p.balance > 0).map(p => p.id);
        const usePayments: Array<number> = !props.customerStore
            ? []
            : props.customerStore!.customer!.account.account.payments.filter(p => p.dateArchived === null && p.balance > 0).map(p => p.id);
        const useDisbursements: Array<number> = !props.customerStore
            ? []
            : props.customerStore!.customer!.account.account.disbursements.filter(p => p.dateArchived === null && p.balance > 0).map(p => p.id);

        return {
            wasValidated: false,
            showInvoicesMessage: false,
            ...EditableSettlement.Empty(coveredInvoices, useProceeds, usePayments, useDisbursements)
        };
    };

    submit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // TODO: Review this, see if we can shrink it down a bit

        //Editing Settlement
        if (this.state.id) {
            if (this.state.type === "DISBURSEMENT") {
                if (this.state.paymentMethod !== null) {
                    this.setState({ wasValidated: false });
                    this.editSettlement();
                } else {
                    this.setState({ wasValidated: true });
                }
            } else {
                if (this.state.currency !== null && this.state.paymentMethod !== null) {
                    this.setState({ wasValidated: false });
                    this.editSettlement();
                } else {
                    this.setState({ wasValidated: true });
                }
            }
            //Adding Settlement
        } else {
            if (this.state.type === "PAYMENT") {
                if (this.state.currency !== null && this.state.paymentMethod !== null && this.state.amount && (this.state.entryDate as Moment).utc) {
                    this.setState({ wasValidated: false });
                    this.createSettlement();
                } else {
                    this.setState({ wasValidated: true });
                    if (
                        this.state.coversInvoices.length === 0 &&
                        this.props.customerStore!.customer!.account.account.invoices.filter(i => i.dateVoided === null).filter(i => i.balance !== 0)
                            .length > 0
                    ) {
                        this.setState({ showInvoicesMessage: true });
                    }
                }
                // Disbursement
            } else if (this.state.type === "DISBURSEMENT") {
                if (this.state.amount && this.state.paymentMethod !== null && (this.state.entryDate as Moment).utc) {
                    this.setState({ wasValidated: false });
                    this.createSettlement();
                } else {
                    this.setState({ wasValidated: true });
                }
            } else {
                this.setState({ wasValidated: true });
            }
        }
    };

    createSettlement = async () => {
        try {
            const request = EditableSettlement.ToCreateRequest(this.state);
            const updatedCustomer = await this.props.customerStore!.createSettlement(request);
            this.props.closeDialog(updatedCustomer);
            successMessage(`Settlement has been created.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    editSettlement = async () => {
        try {
            const request = EditableSettlement.ToEditRequest(this.state);
            const updatedCustomer = await this.props.customerStore!.editSettlementDetails(this.state.id!, request);
            this.props.closeDialog(updatedCustomer);
            successMessage(`Changes successfully saved.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    closeDialog = (): void => {
        this.props.closeDialog();
    };

    handleOnSelect = (type: "coversInvoices" | "useProceeds" | "usePayments" | "useDisbursements") => (id: number, isSelect: boolean): void => {
        if (isSelect) {
            this.setState({
                [type]: [...this.state[type], id]
            } as any);
        } else {
            this.setState({
                [type]: this.state[type].filter(x => x !== id)
            } as any);
        }
    };

    handleOnSelectAll = (type: "coversInvoices" | "useProceeds" | "usePayments" | "useDisbursements") => (
        isSelect: boolean,
        ids: Array<any>
    ): void => {
        if (isSelect) {
            this.setState({ [type]: ids } as any);
        } else {
            this.setState({ [type]: [] } as any);
        }
    };

    componentDidUpdate(prevProps: Props, prevState: State): void {
        if (this.props.editableSettlement && prevProps.editableSettlement !== this.props.editableSettlement) {
            this.setState({
                ...EditableSettlement.FromModel(this.props.editableSettlement)
            });
        } else if (this.props.showSettlementDialog && !prevProps.showSettlementDialog) {
            this.setState(this.emptyState());
        }
    }

    componentDidMount() {
        if (this.props.showSettlementDialog && this.props.editableSettlement) {
            this.setState({
                ...EditableSettlement.FromModel(this.props.editableSettlement)
            });
        } else if (this.props.showSettlementDialog) {
            this.setState(this.emptyState());
        }
    }

    // TODO CHECK DATE VALDIATION
    getValidDate = (date: any) => {
        if (this.props.applicationsOpen) {
            return moment(date).isSameOrAfter(moment(this.props.applicationsOpen));
        } else {
            return true;
        }
    };

    enableFieldsOnEdit = (): boolean => {
        // TODO: Not sure the intention on this
        const state = this.state;
        return state.id !== null && state.type === null && state.currency === null && state.amount === null && state.paymentMethod === null;
    };

    render() {
        const { customerStore } = this.props;
        if (!customerStore) {
            return <div />;
        }

        const { configurationStore } = this.props;
        if (!configurationStore) {
            return <div />;
        }

        const { customer } = customerStore;
        if (!customer) {
            return null;
        }

        const enableFieldsOnEdit = this.enableFieldsOnEdit();
        const areWeEditing = this.props.editableSettlement !== undefined;

        const proceeds = customer.account.account.proceeds;
        const invoices = customer.account.account.invoices;
        const disbursements = customer.account.account.disbursements;
        const payments = customer.account.account.payments;

        // TODO: Check if ID is the ideal sorting here (versus issueDate/dueDate/etc.)
        const proceedsData = proceeds.filter(p => p.dateArchived === null && p.balance > 0).sort((a: Proceeds, b: Proceeds) => a.id - b.id);
        const paymentsData = payments.filter(p => p.dateArchived === null && p.balance > 0).sort((a: Payment, b: Payment) => a.id - b.id);
        const disbursementsData = disbursements
            .filter(d => d.dateArchived === null && d.balance > 0)
            .sort((a: Disbursement, b: Disbursement) => a.id - b.id);
        const invoicesData = invoices
            .filter(i => i.dateVoided === null)
            .filter(i => i.balance !== 0)
            .sort((a: Invoice, b: Invoice) => a.id - b.id);

        const disbursementsBalance = disbursementsData
            .filter(d => this.state.useDisbursements.indexOf(d.id) >= 0)
            .reduce((total, item) => total + item.balance, 0);
        const paymentBalance = paymentsData.filter(p => this.state.usePayments.indexOf(p.id) >= 0).reduce((total, item) => total + item.balance, 0);
        const invoicesBalance = invoicesData
            .filter(i => this.state.coversInvoices.indexOf(i.id) >= 0)
            .reduce((total, item) => total + item.balance, 0);
        const proceedsBalance = proceedsData.filter(i => this.state.useProceeds.indexOf(i.id) >= 0).reduce((total, item) => total + item.balance, 0);

        const currentAmountWithSign = (this.state.type === "DISBURSEMENT" ? 1 : -1) * ConvertToCents(this.state.amount || "0");
        const settlementBalance = invoicesBalance - paymentBalance + disbursementsBalance - proceedsBalance;
        const updatedBalance = settlementBalance + currentAmountWithSign;

        const paymentMethods = this.state.type === "DISBURSEMENT" ? DisbursementPamentMethods() : PaymentPamentMethods();

        const loading = customerStore.loading;

        return (
            <>
                <FormDialog
                    title={`Settlement for ${ExtractFriendlyName(customer)}`}
                    modalSize={this.props.editableSettlement ? "modal-md" : "modal-xl"}
                    show={this.props.showSettlementDialog}
                    onClose={this.closeDialog}
                >
                    <form
                        className={"px-2 needs-validation " + (this.state.wasValidated === true ? "was-validated" : "")}
                        noValidate={true}
                        onSubmit={this.submit}
                        ref={ref => (this.form = ref)}
                    >
                        <div className="form-row">
                            <div className={`col-md-12 ${this.props.editableSettlement ? "" : "col-lg-4"}`}>
                                <div className="card">
                                    <div className="card-header">Settlement Details</div>
                                    <div className="card-body">
                                        {invoices.length > 0 && (
                                            <div className="form-row">
                                                <div className="col-12">
                                                    <div className="alert alert-warning text-center" role="alert">
                                                        <FontAwesomeIcon icon={faInfo} />
                                                        &nbsp; Please review selected <em>Outstanding Invoices</em> to include in this settlement.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="form-row">
                                            <DateGroup
                                                label="Transaction Date:"
                                                invalidFeedBack="Please provide a date."
                                                name="entryDate"
                                                className="col-12"
                                                value={this.state.entryDate}
                                                disabled={areWeEditing || loading}
                                                onChange={e => this.setState({ entryDate: e })}
                                                dateFormat="YYYY-MM-DD"
                                                isRequired={true}
                                                icon={true}
                                                isValidDate={this.getValidDate}
                                                forceInvalid={typeof this.state.entryDate === "string"}
                                            />
                                        </div>
                                        <div className="form-row">
                                            <SelectGroup
                                                label="Type:"
                                                name="type"
                                                isRequired={true}
                                                isInvalid={this.state.wasValidated && this.state.type === null}
                                                className="col-12"
                                                disabled={areWeEditing || loading}
                                                value={this.state.type || ""}
                                                onChange={e => {
                                                    e.preventDefault();
                                                    const type = e.target.value === "" ? null : e.target.value;
                                                    this.setState({ type, paymentMethod: null });
                                                }}
                                            >
                                                {this.state.type === null && <option value="">Select Type...</option>}
                                                {Object.keys(SettlementType).map(type => (
                                                    <option key={type} value={type}>
                                                        {SettlementTypeFriendlyName(type as SettlementType)}
                                                    </option>
                                                ))}
                                            </SelectGroup>
                                        </div>
                                        <div className="form-row">
                                            <SelectGroup
                                                label="Currency:"
                                                name="currency"
                                                isRequired={this.state.type === "PAYMENT"}
                                                isInvalid={this.state.wasValidated && this.state.type === "PAYMENT" && this.state.currency === null}
                                                className="col-12"
                                                disabled={this.state.type === "DISBURSEMENT" || enableFieldsOnEdit || loading || false}
                                                value={this.state.type === "DISBURSEMENT" ? "CAD" : this.state.currency || ""}
                                                onChange={e => {
                                                    e.preventDefault();
                                                    const currency = e.target.value === "" ? null : e.target.value;
                                                    this.setState({ currency });
                                                }}
                                            >
                                                {this.state.currency === null && <option value="">Select Currency...</option>}
                                                {Object.keys(Currency).map(currency => (
                                                    <option key={currency} value={currency}>
                                                        {currency}
                                                    </option>
                                                ))}
                                            </SelectGroup>
                                        </div>
                                        <div className="form-row">
                                            <InputGroup
                                                label="Amount (CAD):"
                                                className="col-12"
                                                type="text"
                                                name="amount"
                                                disabled={(!areWeEditing ? (this.state.type !== null ? enableFieldsOnEdit : true) : true) || loading}
                                                isRequired={true}
                                                isInvalid={isNaN(currentAmountWithSign)}
                                                value={this.state.amount}
                                                onChange={e => {
                                                    const amount = e.target.value;
                                                    this.setState({ amount: amount });
                                                }}
                                            />
                                        </div>
                                        {this.props.cadToUsdExchangeRate && this.state.currency === Currency.USD && this.state.amount !== "" && (
                                            <div className="form-row mb-3">
                                                <div className="col-12">
                                                    <label htmlFor="" className="text-info">
                                                        Amount (US)
                                                    </label>
                                                    <input
                                                        readOnly={true}
                                                        className="form-control text-info"
                                                        type="text"
                                                        value={CovertAndFormatCentsToDollars(
                                                            (parseFloat(this.state.amount) / this.props.cadToUsdExchangeRate) * 100
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="form-row">
                                            <SelectGroup
                                                label="Payment Method:"
                                                name="paymentMethod"
                                                isRequired={true}
                                                isInvalid={this.state.wasValidated && this.state.paymentMethod === null}
                                                className="col-12"
                                                disabled={loading}
                                                value={this.state.paymentMethod || ""}
                                                onChange={e => {
                                                    e.preventDefault();
                                                    const pm = e.target.value === "" ? null : e.target.value;
                                                    this.setState({ paymentMethod: pm });
                                                }}
                                            >
                                                {this.state.paymentMethod === null && <option value="">Select Payment Method...</option>}
                                                {paymentMethods.map(pm => (
                                                    <option key={pm} value={pm}>
                                                        {PaymentMethodFriendlyName(pm)}
                                                    </option>
                                                ))}
                                            </SelectGroup>
                                        </div>
                                        <div className="form-row">
                                            <TextareaGroup
                                                label="Notes"
                                                rows={3}
                                                isRequired={false}
                                                name="notes"
                                                className="form-group col-12"
                                                value={this.state.notes || ""}
                                                onChange={e => this.setState({ notes: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-row text-right">
                                            <button type="button" className="btn btn-secondary mr-auto" onClick={this.closeDialog} disabled={loading}>
                                                Cancel
                                            </button>
                                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                                {this.props.editableSettlement && this.props.editableSettlement.id
                                                    ? "Edit Settlement"
                                                    : `Add Settlement`}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {!this.props.editableSettlement && (
                                <div className="col-md-12 col-lg-8">
                                    <div className="card">
                                        <div className="card-header">Settlement Overview</div>
                                        <div className="card-body">
                                            {disbursementsData.length > 0 && (
                                                <SimpleSettlementTable
                                                    keyField={"otherDisbursementsOnDialog"}
                                                    title="Over-Disbursements"
                                                    tableType="OTHER_DISBURSEMENTS"
                                                    data={disbursementsData}
                                                    disableSelection={areWeEditing}
                                                    selected={this.state.useDisbursements}
                                                    handleOnSelect={this.handleOnSelect("useDisbursements")}
                                                    handleOnSelectAll={this.handleOnSelectAll("useDisbursements")}
                                                    balance={disbursementsBalance}
                                                />
                                            )}
                                            {paymentsData.length > 0 && (
                                                <SimpleSettlementTable
                                                    keyField={"otherPaymentsOnDialog"}
                                                    title="Over-Payments"
                                                    tableType="OTHER_PAYMENTS"
                                                    data={paymentsData}
                                                    disableSelection={areWeEditing}
                                                    selected={this.state.usePayments}
                                                    handleOnSelect={this.handleOnSelect("usePayments")}
                                                    handleOnSelectAll={this.handleOnSelectAll("usePayments")}
                                                    balance={paymentBalance}
                                                />
                                            )}
                                            {proceeds.length > 0 && (
                                                <SimpleSettlementTable
                                                    keyField={"proceedsOnDialog"}
                                                    title="Proceeds from Sale with Outstanding Amounts"
                                                    tableType="PROCEEDS"
                                                    data={proceedsData}
                                                    disableSelection={areWeEditing}
                                                    selected={this.state.useProceeds}
                                                    handleOnSelect={this.handleOnSelect("useProceeds")}
                                                    handleOnSelectAll={this.handleOnSelectAll("useProceeds")}
                                                    balance={proceedsBalance}
                                                />
                                            )}
                                            {invoices.length > 0 && (
                                                <SimpleSettlementTable
                                                    keyField={"invoicesOnDialog"}
                                                    title="Outstanding Invoices"
                                                    tableType="INVOICES"
                                                    data={invoicesData}
                                                    disableSelection={areWeEditing}
                                                    selected={this.state.coversInvoices}
                                                    handleOnSelect={this.handleOnSelect("coversInvoices")}
                                                    handleOnSelectAll={this.handleOnSelectAll("coversInvoices")}
                                                    balance={invoicesBalance}
                                                />
                                            )}
                                            {!areWeEditing && (
                                                <div className="form-row px-2 py-2 alert alert-dark">
                                                    {/* <StatementNumbers title="Account Balance" value={balance} styleValue="success"/>  */}
                                                    {invoicesBalance !== 0 && (
                                                        <StatementNumbers
                                                            title="Selected Invoice Balance"
                                                            value={invoicesBalance}
                                                            styleValue="danger"
                                                        />
                                                    )}
                                                    {paymentBalance !== 0 && (
                                                        <StatementNumbers title="Overpayments Used" value={-paymentBalance} styleValue="success" />
                                                    )}
                                                    {proceedsBalance !== 0 && (
                                                        <StatementNumbers title="Proceeds Used" value={-proceedsBalance} styleValue="success" />
                                                    )}
                                                    {this.state.type && (
                                                        <StatementNumbers
                                                            title={`Current ${SettlementTypeFriendlyName(this.state.type)}`}
                                                            value={isNaN(currentAmountWithSign) ? 0 : currentAmountWithSign}
                                                            styleTitle="font-weight-bold"
                                                            styleValue={
                                                                this.state.type === "DISBURSEMENT"
                                                                    ? `danger font-weight-bold`
                                                                    : `success font-weight-bold`
                                                            }
                                                        />
                                                    )}
                                                    {disbursementsBalance > 0 && (
                                                        <StatementNumbers
                                                            title="Over Disbursements"
                                                            value={disbursementsBalance}
                                                            styleValue="danger"
                                                        />
                                                    )}
                                                    <StatementNumbers
                                                        title="Initial Settlement Balance"
                                                        value={isNaN(settlementBalance) ? 0 : settlementBalance}
                                                        styleTitle="info"
                                                        styleValue="info"
                                                    />
                                                    {settlementBalance !== updatedBalance && (
                                                        <StatementNumbers
                                                            title="Updated Settlement Balance"
                                                            value={isNaN(updatedBalance) ? 0 : updatedBalance}
                                                            styleTitle="secondary"
                                                            styleValue="secondary"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </form>
                </FormDialog>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/SettlementDialog.tsx